import type { ReactNode } from 'react';
import { DevicePreviewToggle, type PreviewDevice } from '@game-platform/theme-constructor';
import type { PreviewTemplate } from '@game-platform/pack-animation';
import type { PoolOfferConfig } from '../../lib/api/operatorConsole';
import { PackSelector } from '../Themes/PackSelector';
import { TemplateSelector } from '../Themes/TemplateSelector';
import type { TemplateName } from '../Themes/useDefaultTemplate';
import type { AnimationScope } from './animationScope';
import { RaritySelector } from './RaritySelector';
import { ScopeSelector } from './ScopeSelector';

interface Props {
  template: PreviewTemplate;
  /** What switching the template means for the edited pack; none in All packs scope. */
  templateHint: string | undefined;
  defaultTemplate: TemplateName;
  onTemplateChange: (template: PreviewTemplate) => void;
  packs: PoolOfferConfig[];
  packId: string;
  onPackChange: (id: string) => void;
  scope: AnimationScope;
  onScopeChange: (scope: AnimationScope) => void;
  packScopeBlocker: string | null;
  /** A write is in flight: what is being edited must not change under it. */
  locked: boolean;
  rarity: string;
  onRarityChange: (rarity: string) => void;
  device: PreviewDevice;
  onDeviceChange: (device: PreviewDevice) => void;
  /** Preview commands and Save, on the right. */
  actions: ReactNode;
}

/** Dark like the theme editor's top bar, so the shared selectors keep their look. */
export function AnimationTopBar(props: Props) {
  return (
    // Wraps to a second row rather than pushing Save off a narrow screen
    <header className="flex min-h-12 shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-neutral-800 bg-neutral-900 px-4 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-2 font-mono text-[11px] uppercase tracking-wide text-neutral-400">
          Pack Opening
        </span>
        <fieldset
          disabled={props.locked}
          className="contents [&_select:disabled]:cursor-not-allowed [&_select:disabled]:opacity-50"
        >
          <span title={props.templateHint}>
            <TemplateSelector
              template={props.template}
              onTemplateChange={props.onTemplateChange}
              defaultTemplate={props.defaultTemplate}
            />
          </span>
          <PackSelector
            packs={props.packs}
            gameId={props.packId}
            onGameIdChange={props.onPackChange}
            markCustomOpening
          />
          <ScopeSelector
            scope={props.scope}
            onChange={props.onScopeChange}
            packBlocker={props.packScopeBlocker}
          />
        </fieldset>
        <RaritySelector value={props.rarity} onChange={props.onRarityChange} />
        <DevicePreviewToggle value={props.device} onChange={props.onDeviceChange} />
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">{props.actions}</div>
    </header>
  );
}
